/**
 * Sidebar — always-dark (#141414) navigation sidebar for the evaluator shell.
 *
 * Purely presentational: props in, JSX out. No fetch, no polling, no global
 * reads. The consumer owns all data and navigation state; this component only
 * renders items and fires onSelect. Designed to be mounted and eyeballed
 * against mock props before any hash-router wiring (Wave 2c).
 *
 * Observatory design ref §2.5, §5.2 — always-dark sidebar, rgba(255,255,255,*)
 * text scale, 2px accent left border on the active item, section-label treatment.
 */

import type { ReactNode } from 'react'

export interface SidebarItem {
  /** Stable identifier used as the active key and React list key. */
  id: string
  /** Human-readable nav label. */
  label: string
  /** Optional icon rendered at 16×16 before the label (presentational). */
  icon?: ReactNode
}

export interface SidebarProps {
  items: SidebarItem[]
  activeId: string
  onSelect: (id: string) => void
}

export function Sidebar({ items, activeId, onSelect }: SidebarProps): JSX.Element {
  return (
    <aside className="sidebar" aria-label="Main navigation">
      <div className="sidebar-header">
        <span className="sidebar-wordmark">Evaluator</span>
      </div>

      <nav className="sidebar-nav">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`sidebar-item${item.id === activeId ? ' sidebar-item--active' : ''}`}
            aria-current={item.id === activeId ? 'page' : undefined}
            onClick={() => onSelect(item.id)}
          >
            {item.icon != null && (
              <span className="sidebar-item-icon" aria-hidden="true">
                {item.icon}
              </span>
            )}
            {item.label}
          </button>
        ))}
      </nav>

      {/* Settings affordance — fixed footer, not a nav item (spec §1A) */}
      <div className="sidebar-footer">
        <button type="button" className="sidebar-item" data-testid="sidebar-settings">
          Settings
        </button>
      </div>
    </aside>
  )
}
